import type { ErrorEntry, ExecutionItem } from './types';
import { causeMeta } from './errorCatalog';

export const BRT_TZ = 'America/Sao_Paulo';

const nf = new Intl.NumberFormat('pt-BR');
const tf = new Intl.DateTimeFormat('pt-BR', { timeZone: BRT_TZ, hour: '2-digit', minute: '2-digit' });
const tfs = new Intl.DateTimeFormat('pt-BR', { timeZone: BRT_TZ, hour: '2-digit', minute: '2-digit', second: '2-digit' });
const dtf = new Intl.DateTimeFormat('pt-BR', { timeZone: BRT_TZ, day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

export const fmtInt = (n: number) => nf.format(Math.round(n));
export const fmtDec = (n: number, d = 1) => n.toLocaleString('pt-BR', { maximumFractionDigits: d });
export const fmtTime = (sec: number, withSeconds = false) => (withSeconds ? tfs : tf).format(new Date(sec * 1000));
export const fmtDateTime = (iso?: string | null) => (iso ? dtf.format(new Date(iso)).replace(',', '') : '—');
export const fmtCompact = (v: number) =>
  v >= 1000 ? `${fmtDec(v / 1000, v >= 10000 ? 0 : 1)} mil` : String(Math.round(v));

export function fmtDuration(sec: number) {
  const s = Math.max(0, Math.round(sec));
  if (s < 60) return `${s}s`;
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), r = s % 60;
  return h ? `${h}h ${String(m).padStart(2, '0')}min` : `${m}min ${r}s`;
}

const toSec = (iso?: string | null) => (iso ? Math.floor(Date.parse(iso) / 1000) : null);
export const cityOf = (tz: string) => (tz.split('/').pop() ?? tz).replace(/_/g, ' ');

export interface CauseCount { key: string; n: number; message: string }

export interface Page {
  id: number;
  pageId: string;
  name: string;
  tz: string;
  app: number;
  ok: boolean;
  /** Status cru do broadcast: 'ok' sozinho não distingue enviado de ainda-não-enviado. */
  status: string;
  /** Disparou de verdade. Sem isto, `s` cai no agendado e a tela inventa a hora. */
  iniciou: boolean;
  concluiu: boolean;
  leads: number;
  sent: number;
  fail: number;
  deliv: number;
  /** epoch seconds */
  s: number;
  e: number;
  sch: number;
  delay: number;
  errs: CauseCount[];
}

/**
 * `error_map` tem DOIS formatos no banco, e os dois chegam aqui.
 *
 * Novo  (lista) : [{ code, subcode, message, count, pending_retry }]
 * Antigo (objeto): { "551": "2022", "100": "70" }  — valores em string
 *
 * Broadcasts anteriores a ~agosto/2026 usam o objeto. Chamar `.forEach` nele
 * lança TypeError dentro de um `useMemo`, o React desmonta a árvore e o
 * sistema INTEIRO vira tela branca — foi o que aconteceu ao abrir o grupo
 * 2185. Formato desconhecido vira lista vazia: a tela abre sem as causas, em
 * vez de derrubar a aplicação.
 */
function lerErrorMap(bruto: ExecutionItem['error_map']): ErrorEntry[] {
  if (!bruto) return [];
  if (Array.isArray(bruto)) return bruto;
  if (typeof bruto === 'object') {
    return Object.entries(bruto as Record<string, unknown>).map(([code, count]) => ({
      code: String(code),
      subcode: null,
      message: '',
      count: Number(count) || 0,
      pending_retry: false,
    }));
  }
  return [];
}

export function normalizeItems(items: ExecutionItem[]): Page[] {
  return items.map((it) => {
    // `s`/`e` seguem com fallback porque alimentam os cálculos de atraso e
    // duração; quem decide o que a tela MOSTRA são as flags abaixo.
    const s = toSec(it.started_at) ?? toSec(it.scheduled_at) ?? 0;
    const e = toSec(it.completed_at) ?? s;
    const iniciou = toSec(it.started_at) != null;
    const concluiu = toSec(it.completed_at) != null;
    const sch = toSec(it.scheduled_at) ?? s;
    const acc = new Map<string, CauseCount>();
    lerErrorMap(it.error_map).forEach((er) => {
      const cur = acc.get(er.code);
      if (cur) cur.n += er.count || 0;
      else acc.set(er.code, { key: er.code, n: er.count || 0, message: er.message });
    });
    return {
      id: it.broadcast_id,
      pageId: it.page_id,
      name: it.page_name,
      tz: it.page_timezone,
      app: it.app_id,
      ok: it.status !== 'failed',
      status: it.status,
      iniciou,
      concluiu,
      leads: it.total_leads,
      sent: it.total_sent,
      fail: it.total_failed,
      deliv: it.total_delivered,
      s, e, sch,
      delay: it.delay_real_min ?? it.delay_minutes ?? Math.max(0, (s - sch) / 60),
      errs: [...acc.values()],
    };
  });
}

export const hasCause = (p: Page, key: string) => p.errs.some((e) => e.key === key);
export const failRate = (fail: number, leads: number) => (leads ? Math.round((fail / leads) * 1000) / 10 : 0);

export function rateTone(rate: number) {
  if (rate >= 20) return { bg: '#fee2e2', fg: '#b91c1c', bar: '#ef4444' };
  if (rate >= 1) return { bg: '#ffedd5', fg: '#c2410c', bar: '#f97316' };
  return { bg: '#f3f4f6', fg: '#4b5563', bar: '#9ca3af' };
}

export function filterPages(pages: Page[], q: string, onlyErrors: boolean, appName: (id: number) => string) {
  const t = q.trim().toLowerCase();
  return pages.filter(
    (p) =>
      (!t ||
        p.name.toLowerCase().includes(t) ||
        p.pageId.includes(t) ||
        String(p.app).includes(t) ||
        appName(p.app).toLowerCase().includes(t)) &&
      (!onlyErrors || p.errs.length > 0),
  );
}

export function timeDomain(pages: Page[]) {
  if (!pages.length) { const now = Math.floor(Date.now() / 1000); return { d0: now - 3600, d1: now }; }
  const d0 = Math.floor(Math.min(...pages.map((p) => Math.min(p.sch, p.s))) / 60) * 60;
  const d1 = Math.ceil((Math.max(...pages.map((p) => p.e)) + 60) / 300) * 300;
  return { d0, d1 };
}

export interface Series { n: number; perMin: { sent: number[]; deliv: number[]; fail: number[] }; cum: { sent: number[]; deliv: number[]; fail: number[] } }

/** Distribui enviados/entregues/falhas de cada página linearmente entre started_at e completed_at.
 *  Se o backend passar a enviar timestamps por lote (first_send_at/last_send_at ou buckets), troque aqui. */
export function calcSeries(pages: Page[], d0: number, d1: number): Series {
  const n = Math.ceil((d1 - d0) / 60) + 1;
  const sent: number[] = Array(n).fill(0), deliv: number[] = Array(n).fill(0), fail: number[] = Array(n).fill(0);
  pages.forEach((p) => {
    const span = Math.max(p.e - p.s, 1), end = p.s + span;
    const frac = (t: number) => Math.min(1, Math.max(0, (t - p.s) / span));
    const b0 = Math.max(0, Math.floor((p.s - d0) / 60)), b1 = Math.min(n - 1, Math.floor((end - 1 - d0) / 60));
    for (let b = b0; b <= b1; b++) {
      const f0 = frac(d0 + b * 60), f1 = b === b1 ? 1 : frac(d0 + (b + 1) * 60);
      // alocação inteira: soma por minuto == total da página, sem frações que somem no arredondamento
      const part = (v: number) => Math.round(v * f1) - Math.round(v * f0);
      sent[b] += part(p.sent); deliv[b] += part(p.deliv); fail[b] += part(p.fail);
    }
  });
  const cum = (a: number[]) => { let s = 0; return a.map((v) => (s += v)); };
  return { n, perMin: { sent, deliv, fail }, cum: { sent: cum(sent), deliv: cum(deliv), fail: cum(fail) } };
}

export interface CauseAgg { key: string; label: string; color: string; description: string; leads: number; pages: number }

export function aggregateCauses(pages: Page[]): CauseAgg[] {
  const m = new Map<string, CauseAgg>();
  pages.forEach((p) =>
    p.errs.forEach((e) => {
      const cur = m.get(e.key);
      if (cur) { cur.leads += e.n; cur.pages += 1; }
      else m.set(e.key, { key: e.key, ...causeMeta(e.key, e.message), leads: e.n, pages: 1 });
    }),
  );
  return [...m.values()].sort((a, b) => b.leads - a.leads || b.pages - a.pages);
}

export interface AppAgg { app: number; pages: Page[]; leads: number; sent: number; fail: number; rate: number; errs: CauseCount[] }

export function aggregateApps(pages: Page[]): AppAgg[] {
  const m = new Map<number, Page[]>();
  pages.forEach((p) => m.set(p.app, [...(m.get(p.app) ?? []), p]));
  return [...m.entries()]
    .map(([app, ps]) => {
      const errs = new Map<string, CauseCount>();
      ps.forEach((p) => p.errs.forEach((e) => {
        const cur = errs.get(e.key);
        if (cur) cur.n += e.n; else errs.set(e.key, { ...e });
      }));
      const leads = ps.reduce((a, p) => a + p.leads, 0), fail = ps.reduce((a, p) => a + p.fail, 0);
      return { app, pages: ps, leads, sent: ps.reduce((a, p) => a + p.sent, 0), fail, rate: failRate(fail, leads), errs: [...errs.values()] };
    })
    .sort((a, b) => b.fail - a.fail);
}

export function groupByTimezone(pages: Page[]) {
  const m = new Map<string, Page[]>();
  pages.forEach((p) => m.set(p.tz, [...(m.get(p.tz) ?? []), p]));
  return [...m.entries()]
    .map(([tz, ps]) => ({ tz, pages: ps.sort((a, b) => a.s - b.s) }))
    .sort((a, b) => a.pages[0].sch - b.pages[0].sch);
}
